import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useStatusBarInfo } from '../../hooks/useStatusBarInfo';

interface MobileFullScreenModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footerContent?: React.ReactNode;
  'data-task-menu'?: boolean; // Add support for data attribute
}

/**
 * A reusable full-screen modal component optimized for mobile displays
 * Used to create consistent full-screen modals across the application
 *
 * Features:
 * - Portal-based rendering to avoid layout issues
 * - Consistent styling with header and optional footer
 * - Fixed positioning covering the entire screen
 * - Backdrop overlay for visual hierarchy
 */
export function MobileFullScreenModal({
  isOpen,
  onClose,
  title,
  children,
  footerContent,
  'data-task-menu': isTaskMenu,
  ...restProps
}: MobileFullScreenModalProps) {
  const { isNativeApp, statusBarHeight, navigationBarHeight } = useStatusBarInfo();

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center"
      onClick={e => {
        // Stop propagation to prevent clicks from reaching elements underneath
        e.stopPropagation();
      }}
    >
      <div
        className="w-full h-full bg-white flex flex-col overflow-hidden border-0"
        data-task-menu={isTaskMenu ? 'true' : undefined}
        onClick={e => e.stopPropagation()} // Prevent clicks from bubbling up
        {...restProps}
      >
        {/* Modal header with title and close button - adjusted for status bar */}
        <div
          className="px-4 border-b border-gray-800 bg-gray-900 flex justify-between items-center"
          style={{
            paddingTop: isNativeApp ? `calc(${statusBarHeight}px + 0.75rem)` : '0.75rem',
            paddingBottom: '0.75rem',
          }}
        >
          <h2 className="text-lg font-medium text-white">{title}</h2>
          <button
            onClick={e => {
              e.stopPropagation();
              onClose();
            }}
            className="text-white hover:text-white/80 transition-colors"
            aria-label="Close modal"
          >
            <X size={16} />
          </button>
        </div>

        {/* Modal content with scrolling */}
        <div className="flex-1 overflow-y-auto">{children}</div>

        {/* Optional footer area for actions - adjusted for navigation bar */}
        {footerContent && (
          <div
            className="p-4 mt-auto"
            style={{
              paddingBottom: isNativeApp ? `calc(${navigationBarHeight}px + 1rem)` : '1rem',
            }}
          >
            {footerContent}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * Standard footer with a "Done" button
 * Extracted as a helper component for consistent styling
 */
export function ModalDoneFooter({ onClose }: { onClose: () => void }) {
  return (
    <button
      onClick={e => {
        e.stopPropagation();
        onClose();
      }}
      className="w-full py-2.5 px-4 bg-blue-500 text-white font-medium rounded-md"
    >
      Done
    </button>
  );
}
